import React, { useContext, useEffect, useRef, useState } from 'react';
import mapApi from '../utils/api/map';
import _ from 'lodash';

export type FilterOption = {
  value: string;
  label: string;
  parentId?: string;
  isSelf?: boolean;
};

type FilterOptionsGroup = {
  [key: string]: FilterOption[];
};

export type FilterSelections = {
  [key: string]: string | string[];
};

export type Project = {
  id: string;
  lat: number;
  lng: number;
  projectName: string;
  [key: string]: any;
};

type MapsContextType = {
  activeFilter: string;
  setActiveFilter: (filter: string) => void;
  appliedFilter: string;
  selectedOptions: FilterSelections;
  setSelectedOptions: (
    updater: FilterSelections | ((prev: FilterSelections) => FilterSelections),
  ) => void;
  searchRef: React.RefObject<HTMLInputElement> | null;
  mapData: Project[];
  projectFilterOptions: FilterOptionsGroup;
  inspectorFilterOptions: FilterOptionsGroup;
  refreshMapData: (optionsOverride?: FilterSelections) => Promise<void>;
  resetData: (filter?: string) => void;
  handleSearchFunc: () => void;
};

const defaultInspectorFilterOptions: FilterOptionsGroup = {
  regions: [{ value: 'all', label: 'All Regions' }],
  regionalManagers: [],
  areaManagers: [],
  inspectors: [],
  unassignedInspectors: [],
};

const defaultProjectFilterOptions: FilterOptionsGroup = {
  regions: [{ value: 'all', label: 'All Regions' }],
  divisions: [],
  clients: [],
  projectTypes: [],
  projectContacts: [],
};

const MapsContext = React.createContext<MapsContextType>({
  activeFilter: 'project',
  setActiveFilter: () => {},
  appliedFilter: 'project',
  selectedOptions: {
    regions: 'all',
    divisions: [],
    clients: [],
    projectTypes: [],
    projectContacts: [],
    regionalManagers: [],
    areaManagers: [],
    inspectors: [],
  },
  setSelectedOptions: () => {},
  searchRef: null,
  mapData: [],
  projectFilterOptions: defaultProjectFilterOptions,
  inspectorFilterOptions: defaultInspectorFilterOptions,
  refreshMapData: async () => {},
  resetData: () => {},
  handleSearchFunc: () => {},
});

export const MapsProvider: React.FC<{
  children: React.ReactNode;
  initialMapData?: Project[];
  onMapDataChange?: (data: Project[]) => void;
  initialSelectedOptions?: FilterSelections;
  initialActiveFilter?: string;
  onSelectedOptionsChange?: (data: FilterSelections) => void;
  onActiveFilterChange?: (filter: string) => void;
}> = ({
  children,
  initialMapData,
  onMapDataChange,
  initialSelectedOptions,
  initialActiveFilter,
  onSelectedOptionsChange,
  onActiveFilterChange,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<FilterSelections>(
    initialSelectedOptions ?? {
      regions: 'all',
      divisions: [],
      clients: [],
      projectTypes: [],
      projectContacts: [],
      regionalManagers: [],
      areaManagers: [],
      inspectors: [],
    },
  );
  const searchRef = useRef<HTMLInputElement>(null);
  const [mapData, setMapData] = useState<Project[]>(initialMapData ?? []);
  const [activeFilter, setActiveFilter] = useState<string>(
    initialActiveFilter ?? 'project',
  );
  // Which filter's selections are currently reflected on the map (last applied via save/reset/search).
  const [appliedFilter, setAppliedFilter] = useState<string>(
    initialActiveFilter ?? 'project',
  );
  const [projectFilterOptions, setProjectFilterOptions] =
    useState<FilterOptionsGroup>(defaultProjectFilterOptions);
  const [inspectorFilterOptions, setInspectorFilterOptions] =
    useState<FilterOptionsGroup>(defaultInspectorFilterOptions);

  // Fetch initial map data on mount
  useEffect(() => {
    if (!initialMapData || initialMapData.length === 0) {
      refreshMapData();
    }
  }, []);

  // Fetch filter options once on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      const [projectRes, inspectorRes] = await Promise.all([
        activeFilter === 'project'
          ? mapApi.getDashboardMapsFilterDropdownProject(selectedOptions)
          : ({} as FilterOptionsGroup),
        activeFilter === 'inspector'
          ? mapApi.getDashboardMapsFilterDropdownInspector(selectedOptions)
          : ({} as FilterOptionsGroup),
      ]);

      setProjectFilterOptions(
        prependDefaultOption(projectRes, defaultProjectFilterOptions),
      );
      setInspectorFilterOptions({
        ...prependDefaultOption(inspectorRes, defaultInspectorFilterOptions),
        unassignedInspectors: inspectorRes.unassignedInspectors || [],
      });
    };

    const timer = setTimeout(() => {
      fetchFilterOptions();
    }, 400);

    return () => clearTimeout(timer);
  }, [selectedOptions, activeFilter]);

  // Helper function to prepend default options
  const prependDefaultOption = (
    fetched: FilterOptionsGroup,
    defaults: FilterOptionsGroup,
  ): FilterOptionsGroup => {
    const merged: FilterOptionsGroup = {};

    for (const key in defaults) {
      merged[key] = [
        ...(defaults[key] || []),
        ...(fetched[key] || []).filter((opt) => opt.value !== 'all'),
      ];
    }

    return merged;
  };

  // Fetch map data when filters change
  const refreshMapData = async (optionsOverride?: FilterSelections) => {
    const opts = optionsOverride ?? selectedOptions;
    let params: any;

    if (activeFilter === 'project') {
      params = {
        search: searchRef.current?.value ?? '',
        regions: opts.regions,
        divisions: opts.divisions,
        clients: opts.clients,
        projectTypes: opts.projectTypes,
        projectContacts: opts.projectContacts,
      };
    } else if (activeFilter === 'inspector') {
      params = {
        search: searchRef.current?.value ?? '',
        regions: opts.regions,
        regionalManagers: opts.regionalManagers,
        areaManagers: opts.areaManagers,
        inspectors: opts.inspectors,
      };
    } else {
      throw new Error(`Unknown type: ${activeFilter}`);
    }
    const res = await mapApi.getProjectsMapsData(params);

    setMapData(res.data || []);
    onMapDataChange?.(res.data || []);
    setAppliedFilter(activeFilter);
  };

  // Reset only the options belonging to the given filter, leaving the other
  // filter's selections intact, then refresh the map with the reset values.
  const resetData = (filter: string = activeFilter) => {
    const resetFields: FilterSelections =
      filter === 'inspector'
        ? { regionalManagers: [], areaManagers: [], inspectors: [] }
        : { divisions: [], clients: [], projectTypes: [], projectContacts: [] };
    const next: FilterSelections = {
      ...selectedOptions,
      regions: 'all',
      ...resetFields,
    };

    handleSetSelectedOptions(next);
    refreshMapData(next);
  };

  // Debounced search function to refresh map data
  const handleSearchFunc = _.debounce(() => {
    refreshMapData();
  }, 1000);

  const handleSetSelectedOptions = (updater: any) => {
    setSelectedOptions((prev: any) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;

      onSelectedOptionsChange?.(next);

      return next;
    });
  };

  const handleSetActiveFilter = (filter: string) => {
    setActiveFilter(filter);
    onActiveFilterChange?.(filter);
  };

  return (
    <MapsContext.Provider
      value={{
        activeFilter,
        setActiveFilter: handleSetActiveFilter,
        appliedFilter,
        selectedOptions,
        setSelectedOptions: handleSetSelectedOptions,
        searchRef,
        mapData,
        projectFilterOptions,
        inspectorFilterOptions,
        refreshMapData,
        resetData,
        handleSearchFunc,
      }}
    >
      {children}
    </MapsContext.Provider>
  );
};

export const useMapsContext = () => {
  const context = useContext(MapsContext);

  if (!context) {
    throw new Error('useMapsContext must be used within a MapsProvider');
  }

  return context;
};
